<template>
  <section class="is-small">
    <div class="container">

      <ui-component-drawer
        v-if="showSideDrawer"
        :drawerTitle="drawerTitle"
        :showDrawer="showSideDrawer"
        :onClickClose="hideDrawer"
        position="right"
      >
        <template v-slot:content>
          <OptionSummary
            v-if="showOptionSummary"
            :optionId="itemId"
          />
        </template>
      </ui-component-drawer>

      <ModalCreateOption
        v-if="showModalCreateOption"
        :showModal="showModalCreateOption"
        :locationId="locationId"
        :onClickCancel="hideModal"
        @dataSaved="optionCreated"
      />

      <ModalDeleteOption
        v-if="showModalDeleteOption"
        :showModal="showModalDeleteOption"
        :locationId="locationId"
        :optionId="selectedOption.Id"
        :onClickCancel="hideModal"
        @dataSaved="optionCreated"
      />
      <ui-page-hero
        id="id_title_impressionphotos"
        :title="'Options'"
      ></ui-page-hero>

      <div class="tabs is-boxed">
        <ul>
          <li :class="{'is-active': optionsPage.selectedCategory === ''}">
            <a @click="optionsPage.selectedCategory = ''">
              <span>All categories</span>
            </a>
          </li>
          <li
            :class="{'is-active': optionsPage.selectedCategory === 'FB'}"
            v-if="locationId > 0"
          >
            <a @click="optionsPage.selectedCategory = 'FB'">
              <span>F&amp;B</span>
            </a>
          </li>
          <li
            :class="{'is-active': optionsPage.selectedCategory === 'AV'}"
            v-if="locationId > 0"
          >
            <a @click="optionsPage.selectedCategory = 'AV'">
              <span>AV</span>
            </a>
          </li>
          <li
            :class="{'is-active': optionsPage.selectedCategory === 'Parking'}"
            v-if="locationId > 0"
          >
            <a @click="optionsPage.selectedCategory = 'Parking'">
              <span>Parking</span>
            </a>
          </li>
          <li
            :class="{'is-active': optionsPage.selectedCategory === 'Other'}"
            v-if="locationId > 0"
          >
            <a @click="optionsPage.selectedCategory = 'Other'">
              <span>Other</span>
            </a>
          </li>
          <li class="tab-search is-flex-grow-1">
            <div class="field-search field has-addons">
              <input
                type="text"
                v-model="optionsPage.searchTerm"
                v-on:keyup.13="searchOptions"
                class="input"
                placeholder="Search options"
              />
              <button
                class="button is-primary"
                :class="{ 'is-loading': isBusy }"
                @click="searchOptions"
              >
                <i class="fas fa-search"></i>
              </button>
            </div>
          </li>
        </ul>
      </div>

      <div class="actionBar has-background-white-ter has-padding has-margin-bottom mb-3">
        <div class="level">
          <div class="level-left"></div>
          <div class="level-right">
            <a
              @click="setShowCreateOption"
              class="icon-text has-text-link"
            >
              <span class="icon">
                <font-awesome-icon :icon="['fas', 'plus']" />
              </span>
              <span>Create option</span>
            </a>
          </div>
        </div>
      </div>

      <div>
        <ui-loader v-if="showLoader" />

        <table
          v-if="!showLoader"
          class="table is-fullwidth is-striped is-hoverable"
        >
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th class="has-text-right">Origin</th>
              <th width="100">&nbsp;</th>
            </tr>
          </thead>
          <transition-group
            name="staggered-fade"
            v-bind:css="false"
            v-on:before-enter="beforeEnter"
            v-on:enter="enter"
            tag="tbody"
          >
            <tr
              v-for="(option, index) in filteredOptions"
              :key="'tr_' + index+'_'+optionsPage.selectedCategory"
              :data-index="index"
            >
              <td>
                <a @click="goToOption(option.Id)">{{ option.Name }}</a>
              </td>
              <td>
                <span>{{ option.CategoryName }}</span>
              </td>
              <td class="has-text-right">
                {{ option.SupplierType }}
              </td>
              <td
                class="has-text-right"
                width="75"
              >
                <div class="field is-grouped has-text-right">
                  <p class="control">
                    <a @click="setShowOptionSummary(option.Id)">
                      <span class="icon">
                        <font-awesome-icon :icon="['fas', 'info-circle']" />
                      </span>
                    </a>
                  </p>

                  <p class="control">
                    <a @click="goToOption(option.Id)">
                      <span class="icon">
                        <font-awesome-icon :icon="['fas', 'edit']" />
                      </span>
                    </a>
                  </p>

                  <p class="control">
                    <a
                      v-if="option.SupplierType !== 'Channel' || isChannelOptions"
                      @click="setShowDeleteOption(option)"
                    >
                      <span class="icon has-text-danger">
                        <font-awesome-icon :icon="['fas', 'trash-alt']" />
                      </span>
                    </a>
                  </p>
                </div>
              </td>
            </tr>
          </transition-group>
        </table>

        <Message
          v-if="!showLoader && options.length === 0"
          :message="'No options found.'"
        />
      </div>
    </div>
  </section>
</template>

<script>
import optionProvider from '@/providers/option'
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex'
import staggeredAnimation from '@/mixins/staggeredAnimation'
import EventBus from '../../eventbus/event-bus'

export default {
  components: {
    Message: () => import('@/components/UI/Message'),
    ModalCreateOption: () => import('@/components/Options/ModalCreateOption'),
    ModalDeleteOption: () => import('@/components/Options/ModalDeleteOption'),
    OptionSummary: () => import('@/components/Options/OptionSummary'),
  },

  mixins: [staggeredAnimation],

  props: {
    locationId: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      drawerTitle: '',
      hasLoaded: false,
      isSearching: false,
      isLoading: true,
      itemId: 0,
      meetingtypeId: 0,
      selectedOption: null,
      showModal: false,
      showModalCreateOption: false,
      showModalDeleteOption: false,
      showOptionSummary: false,
      showSideDrawer: false,
    }
  },

  computed: {
    ...mapGetters('optionStore', ['isBusy']),
    ...mapState('channelStore', ['channel']),
    ...mapState('optionStore', ['optionsPage', 'options']),

    isChannelOptions() {
      return this.locationId === 0
    },

    filteredOptions() {
      let options = this.options
      if (
        this.locationId > 0 &&
        this.options &&
        this.optionsPage.selectedCategory !== '' &&
        this.options.length > 0
      ) {
        options = this.options
        options = options.filter(
          (o) => o.Location.CategoryId === this.optionsPage.selectedCategory
        )
      }

      return options
    },

    showLoader() {
      return this.isBusy || this.isLoading
    }
  },

  created() {
    this.searchOptions()
  },

  methods: {
    ...mapMutations('optionStore', ['setOptionsPage', 'setOptions']),
    ...mapActions('optionStore', ['getLocationOptions']),

    searchOptions() {
      this.optionsPage.locationId = this.locationId
      this.optionsPage.channelId = this.locationId > 0 ? 0 : this.channel.Id

      this.setOptionsPage(this.optionsPage)

      if (this.locationId > 0) {
        this.getLocationOptions()
      } else {
        this.getChannelOptions()
      }
    },

    setSelectedCategory(val) {
      this.optionsPage.selectedCategory = val
    },

    getLocationOptions() {
      this.isLoading = true
      optionProvider.methods
        .getLocationOptions(this.locationId, 0, this.optionsPage.searchTerm)
        .then((response) => {
          this.setOptions(response.data.Results)
        })
        .catch((e) => { })
        .finally(() => {
          this.isLoading = false
        })
    },

    getChannelOptions() {
      this.isLoading = true
      optionProvider.methods
        .getChannelOptions(this.channel.Id, 0, this.optionsPage.searchTerm)
        .then((response) => {
          this.setOptions(response.data.Results)
        })
        .catch((e) => { })
        .finally(() => {
          this.isLoading = false
        })
    },

    optionCreated() {
      let self = this

      self.searchOptions('')

      EventBus.$emit('showToast', {
        type: 'info',
        message: 'New option created!',
      })
    },

    setShowDeleteOption(option) {
      this.selectedOption = option
      this.showModalDeleteOption = true
    },

    setShowCreateOption() {
      this.showModal = true
      this.showModalCreateOption = true

      this.modalTitle = 'Create new option'
    },

    hideModal() {
      this.showModal = false
      this.showModalCreateOption = false
      this.showModalDeleteOption = false

      this.modalTitle = ''
      this.selectedOption = null
    },

    setShowOptionSummary(optionId) {
      this.itemId = optionId
      this.drawerTitle = 'Option detail'
      this.showSideDrawer = true
      this.showOptionSummary = true
    },

    hideDrawer() {
      this.itemId = 0
      this.drawerTitle = ''
      this.showSideDrawer = false
      this.showOptionSummary = false
    },

    goToOption(optionId) {
      if (this.locationId > 0) {
        this.$router.push({
          name: 'locations-location-options-detail',
          params: { optionId: optionId },
        })
      } else {
        this.$router.push({
          name: `options-detail`,
          params: { optionId: optionId },
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/template.scss";
.ui-drawer-component {
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 100;

  .drawer {
    display: flex;
    flex-direction: column;
    width: 0; /* 0 width - change this with JavaScript */
    position: fixed; /* Stay in place */
    top: 0; /* Stay at the top */
    left: auto;
    right: auto;
    background-color: white;
    height: 100vh;
    max-width: 420px;
    transition: 0.75s;
    animation-duration: 0.5s;
    @include box-shadow();

    &.right {
      right: 0;
      width: 100%;
    }

    &-minimal {
      width: auto;
    }

    .drawer-head {
      align-items: center;
      background-color: #eeeeee;
      display: flex;
      flex-shrink: 0;
      justify-content: flex-start;
      padding: 15px;
      position: relative;

      .drawer-title {
        flex-grow: 1;
        flex-shrink: 0;
        font-size: $size-4;
      }
    }

    .drawer-body {
      overflow: auto;
    }
  }
}
</style>
